// 共享移动端模块顶栏 — 56px 高，AntD circle back 按钮
//
// 配合 MobileFirstLayout 的 ROUTES_WITH_OWN_HEADER 允许列表使用：
// 列表中的路由会让 layout 跳过自己的 MobileModuleHeader，由模块自己渲染本组件。
//
// 用法：
//   <MobileHeader title="数据中心" onBack={...} extra="收藏夹" onExtraClick={...} />
//
// API 设计目标：
// - 默认 onBack = navigate(-1)
// - extra 可以是字符串（普通文字按钮）或 React node（自定义）
// - 触摸目标 ≥ 44×44

import { Button, Typography } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { colors } from '../theme/colors.js';
import { mobileFontSize, mobileFontWeight } from '../theme/mobile-typography.js';

export interface MobileHeaderProps {
	title: string;
	/** 自定义返回回调，默认 navigate(-1) */
	onBack?: () => void;
	/** 隐藏返回按钮（如：这是路由根 / tab 页） */
	noBack?: boolean;
	/** 右侧文字按钮 */
	extra?: string;
	/** 文字按钮点击 */
	onExtraClick?: () => void;
	/** 右侧自定义节点（覆盖 extra） */
	extraSlot?: ReactNode;
}

const HEADER_HEIGHT = 56;

export default function MobileHeader({
	title,
	onBack,
	noBack,
	extra,
	onExtraClick,
	extraSlot,
}: MobileHeaderProps) {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const handleBack = onBack ?? (() => navigate(-1));

	return (
		<header
			className="ulucu-mobile-header"
			style={{
				display: 'flex',
				alignItems: 'center',
				height: HEADER_HEIGHT,
				padding: '0 8px',
				background: colors.bg.card,
				borderBottom: `1px solid ${colors.graphic.divider}`,
				position: 'sticky',
				top: 0,
				zIndex: 10,
			}}
		>
			{noBack ? (
				<span style={{ width: 44 }} />
			) : (
				<Button
					type="text"
					shape="circle"
					icon={<LeftOutlined style={{ color: colors.primary.text, fontSize: 18 }} />}
					onClick={handleBack}
					aria-label={t('common.back')}
					style={{ minWidth: 44, minHeight: 44 }}
				/>
			)}
			<Typography.Title
				className="ulucu-mobile-header-title"
				level={4}
				style={{
					flex: 1,
					textAlign: 'center',
					margin: 0,
					fontSize: mobileFontSize.topBar,
					fontWeight: mobileFontWeight.medium,
					whiteSpace: 'nowrap',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					color: colors.text.primary,
				}}
			>
				{title}
			</Typography.Title>
			{extraSlot ? (
				<span style={{ minWidth: 44, display: 'flex', justifyContent: 'flex-end' }}>{extraSlot}</span>
			) : extra ? (
				<Button
					type="text"
					onClick={onExtraClick}
					style={{
						color: colors.primary.text,
						fontSize: mobileFontSize.body,
						fontWeight: mobileFontWeight.medium,
						minHeight: 44,
						minWidth: 44,
					}}
				>
					{extra}
				</Button>
			) : (
				<span style={{ width: 44 }} />
			)}
		</header>
	);
}

export const MOBILE_HEADER_HEIGHT = HEADER_HEIGHT;
